// EmployerCheckIns.jsx
// In EmployerDashboard.jsx:
//   import EmployerCheckIns from './EmployerCheckIns'
//   {nav === 'checkins' && <EmployerCheckIns t={t} employees={employees} checkIns={checkIns} />}

import { useState } from 'react'

function moodEmoji(m) { return { great:'😄', good:'🙂', okay:'😐', tough:'😔' }[m] || '—' }
function moodColor(m) { return { great:'#059669', good:'#4F46E5', okay:'#D97706', tough:'#EF4444' }[m] || '#64748B' }
function moodBg(m)    { return { great:'#ECFDF5', good:'#EFF6FF', okay:'#FFFBEB', tough:'#FEF2F2' }[m] || '#F8F9FA' }
function fmtTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })
}
function fmtDate(d) {
  if (!d) return ''
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })
}

export default function EmployerCheckIns({ t, employees, checkIns }) {
  const [filter, setFilter]       = useState('all')
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [search, setSearch]       = useState('')

  const today = new Date().toISOString().split('T')[0]
  const todayRecs = checkIns.filter(c => c.date === today)

  // Per-employee today status
  const empStatus = {}
  employees.forEach(e => {
    const rec = todayRecs.find(c => c.employee_id === e.id)
    empStatus[e.id] = {
      rec,
      in:  !!rec?.checkin_submitted_at,
      out: !!rec?.checkout_submitted_at,
    }
  })

  const inCount  = employees.filter(e => empStatus[e.id].in && !empStatus[e.id].out).length
  const outCount = employees.filter(e => empStatus[e.id].out).length
  const absentCount = employees.filter(e => !empStatus[e.id].in).length

  // Mood distribution today
  const moods = {}
  todayRecs.forEach(r => {
    const m = r.checkin_mood || r.mood
    if (m) moods[m] = (moods[m] || 0) + 1
  })

  // Recent history for selected employee
  const history = selectedEmp
    ? checkIns.filter(c => c.employee_id === selectedEmp.id)
    : []

  const filtered = employees.filter(e => {
    const s = empStatus[e.id]
    if (filter === 'in'     && !(s.in && !s.out)) return false
    if (filter === 'out'    && !s.out)              return false
    if (filter === 'absent' && s.in)               return false
    if (search && !e.full_name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  if (selectedEmp) {
    const s = empStatus[selectedEmp.id]
    return (
      <div>
        <button onClick={() => setSelectedEmp(null)}
          style={{ fontSize:13, color:t.textMuted, background:'none', border:'none', cursor:'pointer', marginBottom:'1.25rem', fontFamily:'Inter,system-ui', display:'flex', alignItems:'center', gap:6 }}>
          ← Back to check-ins
        </button>

        {/* Employee header */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow, display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'#fff', flexShrink:0 }}>
            {selectedEmp.full_name?.charAt(0)}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:20, fontWeight:800, color:t.text }}>{selectedEmp.full_name}</div>
            <div style={{ fontSize:13, color:t.textMuted }}>{selectedEmp.position || selectedEmp.email} {selectedEmp.department && `· ${selectedEmp.department}`}</div>
          </div>
          {/* Today's status */}
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Today</div>
            <div style={{ display:'flex', gap:8 }}>
              <span style={{ fontSize:12, fontWeight:700, padding:'4px 10px', borderRadius:99, background:s.in?'#ECFDF5':'#FEF2F2', color:s.in?'#059669':'#EF4444' }}>
                {s.in ? `✓ In ${fmtTime(s.rec?.checkin_submitted_at)}` : '✗ Not checked in'}
              </span>
              {s.in && (
                <span style={{ fontSize:12, fontWeight:700, padding:'4px 10px', borderRadius:99, background:s.out?'#ECFDF5':'#FFFBEB', color:s.out?'#059669':'#D97706' }}>
                  {s.out ? `✓ Out ${fmtTime(s.rec?.checkout_submitted_at)}` : '⏳ Not checked out'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Today's check-in detail */}
        {s.rec && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
            {/* Morning check-in */}
            {s.rec.checkin_submitted_at && (
              <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text }}>🌅 Morning Check-in</div>
                  <span style={{ fontSize:11, color:t.textMuted }}>{fmtTime(s.rec.checkin_submitted_at)}</span>
                </div>
                {s.rec.checkin_mood && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'8px 12px', background:moodBg(s.rec.checkin_mood), borderRadius:8 }}>
                    <span style={{ fontSize:22 }}>{moodEmoji(s.rec.checkin_mood)}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:moodColor(s.rec.checkin_mood), textTransform:'capitalize' }}>{s.rec.checkin_mood}</span>
                  </div>
                )}
                {s.rec.plan_today && (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Plan for today</div>
                    <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.6 }}>{s.rec.plan_today}</p>
                  </div>
                )}
                {s.rec.challenges && (
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:'#D97706', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Anticipated challenges</div>
                    <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.6 }}>{s.rec.challenges}</p>
                  </div>
                )}
              </div>
            )}

            {/* Evening checkout */}
            {s.rec.checkout_submitted_at && (
              <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text }}>🌙 Evening Check-out</div>
                  <span style={{ fontSize:11, color:t.textMuted }}>{fmtTime(s.rec.checkout_submitted_at)}</span>
                </div>
                {s.rec.mood && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'8px 12px', background:moodBg(s.rec.mood), borderRadius:8 }}>
                    <span style={{ fontSize:22 }}>{moodEmoji(s.rec.mood)}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:moodColor(s.rec.mood), textTransform:'capitalize' }}>{s.rec.mood}</span>
                  </div>
                )}
                {s.rec.done_today && (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Accomplished</div>
                    <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.6 }}>{s.rec.done_today}</p>
                  </div>
                )}
                {s.rec.blockers && (
                  <div style={{ background:'#FEF2F2', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#EF4444', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>⚠️ Blockers</div>
                    <p style={{ fontSize:13, color:'#DC2626', margin:0, lineHeight:1.6 }}>{s.rec.blockers}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Check-in history */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', boxShadow:t.shadow }}>
          <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1rem' }}>Check-in History</div>
          {history.length === 0 ? (
            <p style={{ fontSize:13, color:t.textMuted }}>No check-in history yet.</p>
          ) : history.map(c => (
            <div key={c.id} style={{ display:'flex', gap:12, padding:'10px 0', borderBottom:`1px solid ${t.border}` }}>
              <div style={{ width:36, height:36, borderRadius:8, background:moodBg(c.mood||c.checkin_mood), display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                {moodEmoji(c.mood || c.checkin_mood)}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:t.text }}>{fmtDate(c.date)}</span>
                  <span style={{ fontSize:11, color:t.textMuted }}>
                    {c.checkin_submitted_at && `In ${fmtTime(c.checkin_submitted_at)}`}
                    {c.checkout_submitted_at && ` · Out ${fmtTime(c.checkout_submitted_at)}`}
                  </span>
                </div>
                {c.done_today && <p style={{ fontSize:12, color:t.textSub, margin:'0 0 3px', lineHeight:1.5 }}>{c.done_today?.slice(0,100)}{c.done_today?.length>100?'…':''}</p>}
                {c.blockers && <p style={{ fontSize:12, color:'#EF4444', margin:0 }}>⚠️ {c.blockers}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.75rem' }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>Check-in / Check-out</h1>
          <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>
            {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
          </p>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search employees…"
          style={{ padding:'9px 14px', background:t.card, border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, color:t.text, outline:'none', fontFamily:'Inter,system-ui', width:200 }} />
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:'1.5rem' }}>
        {[
          { label:'Checked In',    value:inCount,      color:'#4F46E5', bg:'#EEF2FF', icon:'🌅' },
          { label:'Checked Out',   value:outCount,     color:'#059669', bg:'#ECFDF5', icon:'✅' },
          { label:'Not Checked In',value:absentCount,  color:'#EF4444', bg:'#FEF2F2', icon:'⏰' },
          { label:'Total Staff',   value:employees.length, color:'#D97706', bg:'#FFFBEB', icon:'👥' },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.color}20`, borderRadius:12, padding:'1rem', textAlign:'center' }}>
            <div style={{ fontSize:24, marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontSize:26, fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:s.color, fontWeight:600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Mood distribution */}
      {Object.keys(moods).length > 0 && (
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:12, padding:'1rem 1.5rem', marginBottom:'1.5rem', boxShadow:t.shadow, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, fontWeight:700, color:t.text }}>Team mood today:</span>
          {Object.entries(moods).map(([m, count]) => (
            <div key={m} style={{ display:'flex', alignItems:'center', gap:6, background:moodBg(m), padding:'5px 12px', borderRadius:99 }}>
              <span style={{ fontSize:18 }}>{moodEmoji(m)}</span>
              <span style={{ fontSize:13, fontWeight:700, color:moodColor(m), textTransform:'capitalize' }}>{m}</span>
              <span style={{ fontSize:12, fontWeight:800, color:moodColor(m) }}>×{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display:'flex', gap:8, marginBottom:'1.25rem' }}>
        {[['all','All'],['in','Checked In'],['out','Checked Out'],['absent','Not In']].map(([v,l]) => (
          <button key={v} onClick={()=>setFilter(v)}
            style={{ padding:'6px 14px', borderRadius:99, fontSize:13, fontWeight:600, cursor:'pointer', border:`1px solid ${filter===v?'#4F46E5':t.border}`, background:filter===v?'#4F46E514':t.card, color:filter===v?'#4F46E5':t.textSub, fontFamily:'Inter,system-ui' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Employee grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:14 }}>
        {filtered.map(emp => {
          const s = empStatus[emp.id]
          const mood = s.rec?.checkin_mood
          const status = s.out ? 'out' : s.in ? 'in' : 'absent'
          const statusStyle = {
            out:    { bg:'#ECFDF5', color:'#059669', label:'✅ Checked out', border:'#A7F3D0' },
            in:     { bg:'#EFF6FF', color:'#4F46E5', label:'🌅 Checked in',  border:'#BFDBFE' },
            absent: { bg:'#FEF2F2', color:'#EF4444', label:'⏰ Not checked in', border:'#FECACA' },
          }[status]

          return (
            <div key={emp.id} onClick={()=>setSelectedEmp(emp)}
              style={{ background:t.card, border:`2px solid ${statusStyle.border}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow, cursor:'pointer' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:'#fff', flexShrink:0 }}>
                  {emp.full_name?.charAt(0)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emp.full_name}</div>
                  <div style={{ fontSize:12, color:t.textMuted }}>{emp.position || emp.email}</div>
                </div>
                {mood && <span style={{ fontSize:22 }}>{moodEmoji(mood)}</span>}
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:99, background:statusStyle.bg, color:statusStyle.color, border:`1px solid ${statusStyle.border}` }}>
                  {statusStyle.label}
                </span>
                {s.in && <span style={{ fontSize:11, color:t.textMuted }}>{fmtTime(s.rec?.checkin_submitted_at)}</span>}
              </div>

              {s.rec?.plan_today && (
                <p style={{ fontSize:12, color:t.textSub, margin:0, lineHeight:1.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  📋 {s.rec.plan_today}
                </p>
              )}
              {s.rec?.blockers && (
                <p style={{ fontSize:12, color:'#EF4444', margin:'4px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  ⚠️ {s.rec.blockers}
                </p>
              )}
              <div style={{ fontSize:11, color:'#4F46E5', fontWeight:600, marginTop:8 }}>View details →</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
